// R3 (renderer) — the block-level text renderer, shared by every surface that
// puts a `DocumentModel` on screen: `DocumentPane` (a whole model as its own
// scroll column) and the mobile interleaved column's original insets (a slice of
// one). One renderer, so the two can never drift apart on the details that other
// subsystems read back out of the DOM:
//
// - `data-block-id` on retelling paragraphs — anchor geometry, hard alignment,
//   `?loc=` reading-position tracking;
// - `data-start` on every block AND every emphasis run — its char offset into
//   `DocumentModel.text`, which is how `dom-offsets` maps a DOM Selection back
//   to model offsets for the gloss popover;
// - block-level `verse-line` elements for continuous-verse originals, so the
//   hard-align hook can inject a margin on a single hexameter.
//
// Sizes are `em`-relative throughout: the surrounding container sets the base
// `font-size` in px and the whole column scales from it.

import { type ReactNode } from "react";
import type {
  DocumentBlock,
  DocumentModel,
} from "@bindery/shared/corpus/document";
import type { PaneId } from "@bindery/shared/corpus/reader";
import { cn } from "@/lib/utils";
import { splitEmphasisRuns, type TextRun } from "./runs";

interface DocumentBlocksProps {
  /** The model the blocks index into (`text`/`emphasis`/`headings` overlays). */
  model: DocumentModel;
  /** The blocks to render, in document order. Defaults to the whole model. */
  blocks?: readonly DocumentBlock[];
  /** Which side this run belongs to; the original reads as a quieter column. */
  pane: PaneId;
}

function renderRuns(runs: readonly TextRun[]): ReactNode[] {
  return runs.map((run) =>
    run.em ? (
      <em key={run.start} data-start={run.start}>
        {run.value}
      </em>
    ) : (
      // A keyed fragment keeps the plain run's offset available without an
      // extra wrapper element in the common (unemphasized) case.
      <span key={run.start} data-start={run.start}>
        {run.value}
      </span>
    ),
  );
}

export function DocumentBlocks({
  model,
  blocks = model.blocks,
  pane,
}: DocumentBlocksProps) {
  const headingLevelByStart = new Map(
    model.headings.map((h) => [h.start, h.level]),
  );
  const isRetelling = pane === "retelling";

  return blocks.map((block, i) => {
    const text = model.text.slice(block.start, block.end);
    if (block.kind === "heading") {
      const level = headingLevelByStart.get(block.start) ?? 1;
      return (
        <h1
          key={`h-${String(block.start)}`}
          data-start={block.start}
          aria-level={level}
          role="heading"
          className={cn(
            "mb-8 font-display font-black tracking-tight text-balance",
            isRetelling
              ? "text-[2.1em] leading-[1.1]"
              : "text-[1.5em] leading-[1.15] text-muted-foreground",
          )}
        >
          {text}
        </h1>
      );
    }
    const runs = splitEmphasisRuns(
      model.text,
      block.start,
      block.end,
      model.emphasis,
    );
    if (block.kind === "verse-line") {
      // Continuous-verse original (e.g. the Odyssey): each metrical line is its
      // own block-level element — no `data-block-id` (originals carry no ids).
      // Tight verse leading with no inter-line gap, and a hanging indent so a
      // long hexameter that wraps tucks its continuation under the line's first
      // character. Block-level so the hard-align hook can inject `marginTop` on
      // it like any other block.
      return (
        <p
          key={`v-${String(block.start)}`}
          data-start={block.start}
          // Deeper hanging indent below `md`: in a phone-width column nearly
          // every hexameter wraps, so at 1.6em the flush/indented alternation
          // stops reading as "line, continuation" and starts reading as noise.
          // The wide original pane keeps the tighter classical indent.
          className="my-0 pl-[2.6em] -indent-[2.6em] leading-[1.35] md:pl-[1.6em] md:-indent-[1.6em]"
        >
          {renderRuns(runs)}
        </p>
      );
    }
    return (
      <p
        key={block.id ?? `p-${String(block.start)}`}
        data-block-id={block.id}
        data-start={block.start}
        className={cn(
          "mb-[1.1em]",
          // First body paragraph after the title gets no top gap.
          i <= 1 && "mt-0",
        )}
      >
        {renderRuns(runs)}
      </p>
    );
  });
}
